<template>
  <v-container fluid class="pl-4 pr-4 pb-4 pt-1">
    <!-- Header -->
    <v-row align="center" class="mb-0 px-0" style="min-height: 48px;">
      <v-col>
        <h4 class="mb-0">LLM Промты</h4>
      </v-col>
      <v-col cols="auto">
        <v-btn
          color="primary"
          variant="elevated"
          @click="openCreateDialog"
          prepend-icon="mdi-plus"
        >
          Добавить промт
        </v-btn>
      </v-col>
    </v-row>

    <!-- Table -->
    <v-card>
      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="prompts"
          :loading="isLoading"
          class="elevation-1"
          hover
          fixed-header
          height="calc(100vh - 236px)"
          hide-default-footer
          :items-per-page="-1"
        >
          <template v-slot:[`item.is_active`]="{ item }">
            <v-chip
              :color="item.is_active ? 'success' : 'grey'"
              size="small"
              variant="flat"
            >
              {{ item.is_active ? 'Активен' : 'Неактивен' }}
            </v-chip>
          </template>

          <template v-slot:[`item.system_prompt`]="{ item }">
            <span class="text-truncate d-inline-block" style="max-width: 400px;">
              {{ item.system_prompt.substring(0, 100) }}...
            </span>
          </template>

          <template v-slot:[`item.updated_at`]="{ item }">
            {{ formatDate(item.updated_at) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn icon size="small" variant="text" @click="openEditDialog(item)">
              <v-icon size="small">mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon size="small" variant="text" color="error" @click="confirmDelete(item)">
              <v-icon size="small">mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialogVisible" max-width="900" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <span>{{ isEditing ? 'Редактировать промт' : 'Новый промт' }}</span>
          <v-spacer />
          <v-btn icon variant="text" @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="form.key"
                label="Ключ"
                variant="outlined"
                density="compact"
                :disabled="isEditing"
                :rules="[v => !!v || 'Обязательное поле']"
                hint="Уникальный идентификатор (например: sentiment_batch)"
                persistent-hint
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="form.name"
                label="Название"
                variant="outlined"
                density="compact"
                :rules="[v => !!v || 'Обязательное поле']"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="form.description"
                label="Описание"
                variant="outlined"
                density="compact"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="form.system_prompt"
                label="Системный промт"
                variant="outlined"
                density="compact"
                rows="12"
                :rules="[v => !!v || 'Обязательное поле']"
                style="font-family: monospace; font-size: 13px;"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="form.user_prompt"
                label="Пользовательский промт (шаблон)"
                variant="outlined"
                density="compact"
                rows="4"
                hint="Используйте {text} или {texts} как плейсхолдеры"
                persistent-hint
                style="font-family: monospace; font-size: 13px;"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="auto">
              <v-switch
                v-model="form.is_active"
                label="Активен"
                color="success"
                density="compact"
                hide-details
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn variant="text" @click="closeDialog">Отмена</v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            @click="savePrompt"
            :loading="isSaving"
            :disabled="!isFormValid"
          >
            {{ isEditing ? 'Сохранить' : 'Создать' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="deleteDialogVisible" max-width="400">
      <v-card>
        <v-card-title>Удалить промт?</v-card-title>
        <v-card-text>
          Промт <strong>{{ deleteTarget?.name }}</strong> ({{ deleteTarget?.key }}) будет удалён. Это действие нельзя отменить.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteDialogVisible = false">Отмена</v-btn>
          <v-btn color="error" variant="elevated" @click="deletePrompt" :loading="isDeleting">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.visible" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import apiClient from '@/apiClient';

export default {
  name: 'PromptsManagement',
  data() {
    return {
      prompts: [],
      isLoading: false,
      isSaving: false,
      isDeleting: false,
      dialogVisible: false,
      deleteDialogVisible: false,
      isEditing: false,
      editingId: null,
      deleteTarget: null,
      form: {
        key: '',
        name: '',
        description: '',
        system_prompt: '',
        user_prompt: '',
        is_active: true,
      },
      headers: [
        { title: 'Ключ', key: 'key', width: '150px' },
        { title: 'Название', key: 'name', width: '250px' },
        { title: 'Системный промт', key: 'system_prompt', sortable: false },
        { title: 'Версия', key: 'version', width: '90px', align: 'center' },
        { title: 'Статус', key: 'is_active', width: '120px', align: 'center' },
        { title: 'Обновлён', key: 'updated_at', width: '150px' },
        { title: '', key: 'actions', sortable: false, width: '100px', align: 'center' },
      ],
      snackbar: { visible: false, text: '', color: 'success' },
    };
  },
  computed: {
    isFormValid() {
      return this.form.key && this.form.name && this.form.system_prompt;
    },
  },
  mounted() {
    this.loadPrompts();
  },
  methods: {
    async loadPrompts() {
      this.isLoading = true;
      try {
        const response = await apiClient.get('/admin/llm-prompts');
        this.prompts = response.data;
      } catch (error) {
        this.showSnackbar('Ошибка загрузки промтов', 'error');
      } finally {
        this.isLoading = false;
      }
    },

    openCreateDialog() {
      this.isEditing = false;
      this.editingId = null;
      this.form = {
        key: '',
        name: '',
        description: '',
        system_prompt: '',
        user_prompt: '',
        is_active: true,
      };
      this.dialogVisible = true;
    },

    openEditDialog(item) {
      this.isEditing = true;
      this.editingId = item.id;
      this.form = {
        key: item.key,
        name: item.name,
        description: item.description || '',
        system_prompt: item.system_prompt,
        user_prompt: item.user_prompt || '',
        is_active: item.is_active,
      };
      this.dialogVisible = true;
    },

    closeDialog() {
      this.dialogVisible = false;
    },

    async savePrompt() {
      this.isSaving = true;
      try {
        if (this.isEditing) {
          await apiClient.put(`/admin/llm-prompts/${this.editingId}`, {
            name: this.form.name,
            description: this.form.description || null,
            system_prompt: this.form.system_prompt,
            user_prompt: this.form.user_prompt || null,
            is_active: this.form.is_active,
          });
          this.showSnackbar('Промт обновлён');
        } else {
          await apiClient.post('/admin/llm-prompts', this.form);
          this.showSnackbar('Промт создан');
        }
        this.closeDialog();
        await this.loadPrompts();
      } catch (error) {
        const msg = error.response?.data?.detail || 'Ошибка сохранения';
        this.showSnackbar(msg, 'error');
      } finally {
        this.isSaving = false;
      }
    },

    confirmDelete(item) {
      this.deleteTarget = item;
      this.deleteDialogVisible = true;
    },

    async deletePrompt() {
      this.isDeleting = true;
      try {
        await apiClient.delete(`/admin/llm-prompts/${this.deleteTarget.id}`);
        this.showSnackbar('Промт удалён');
        this.deleteDialogVisible = false;
        await this.loadPrompts();
      } catch (error) {
        this.showSnackbar('Ошибка удаления', 'error');
      } finally {
        this.isDeleting = false;
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    },

    showSnackbar(text, color = 'success') {
      this.snackbar = { visible: true, text, color };
    },
  },
};
</script>
